/**
 * Device/environment/profile tool dispatch for the Realtime voice hook -
 * split out of useRealtimeVoice.js (2026-08-22) to keep that file from
 * growing further; same dispatch contract as realtimeOperationsTools.js's
 * executeOperationsTool(): returns a result object for names it handles.
 *
 * update_preferred_name is the one "authoritative mutation" tool live here
 * (durable resident-profile change) - it refuses to save when the calling
 * turn was flagged `turn_suspect` (the resident's speech onset overlapped
 * Aria's own audio, per useRealtimeVoice.js's echo/VAD-overlap tracking),
 * asking the resident to repeat themselves instead of trusting a possibly
 * phantom transcript. A false transcript must never silently become a
 * durable profile fact.
 */
import { checkEnding, ENDING_PHRASES } from "./endingPhrases";
import { isSoftEnding } from "./endIntent";
import { API } from "./api";
import { handleToggleLight } from "./realtimeLightControl";
import { roomStatusResult } from "./realtimeRoomStatus";

export { describeDevice } from "./realtimeRoomStatus";
import { findDeviceGuarded } from "./deviceAmbiguity";
import { VOLUME_PHRASES, ROOM_CONTROL_TOOLS, executeRoomControlTool } from "./realtimeRoomControls";
import { shapeResearchResult } from "./researchShape";
import { executeTvRemoteSetup } from "./realtimeTvSetup";

// IMPORTANT: the backend `/devices/.../command` endpoint validates `action`
// against a strict enum (power | brightness | temperature | fan_speed |
// volume | channel | color | color_temp | position). The AI tools speak in
// human terms (state="on", target_f=72) so this layer translates between
// them. Mismatch = HTTP 422 = silent failure where CAOS promises an action
// that never ran.
// Every room command carries the conversation's session_id (light, TV,
// thermostat alike - SC-12) so device_commands and receipts link back to
// the conversation; a turn with no session sends null, never a made-up id.
async function postRoomCommand(room, action, value, kind, sessionId, deviceId) {
  const r = await fetch(`${API}/devices/public/room/${encodeURIComponent(room)}/command`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action, value, kind, session_id: sessionId || null, device_id: deviceId || null }),
  });
  return r;
}

// Real, if reversible, room-state changes - gated the same way as staff
// requests so a noise-hallucinated turn can't move them either.
const CONSEQUENTIAL_DEVICE_TOOLS = new Set(["adjust_room_temperature", "toggle_light", "toggle_tv", "set_timer", "set_tv_input", "configure_tv_remote", ...ROOM_CONTROL_TOOLS]);

// 2026-08-30 (real live incident): mark_resting fired on "No, I can't." -
// a resident PROTESTING being left alone - and end_call fired on "It's
// gonna find me.", ending the whole session. Neither tool had any
// grounding requirement; the model verbally said one thing ("I'll give
// you some space to rest") with no tool call, then invoked the real tool
// one turn later against an utterance that doesn't support it. Same
// class of gap as update_preferred_name's guard above - the tool must be
// traceable to the resident's own words matching a real dismissal/ending
// phrase, not fire on an ambiguous or negatively-phrased turn.
const RESTING_PHRASES = /\b(be quiet|quiet down|let me rest|resting|give me\s*(some\s*)?space|don'?t talk|going to sleep|i'?m\s*(going to |gonna\s*)?sleep|take a nap|napping|i'?m tired|leave me alone|need\s*(some\s*)?(space|quiet))\b/i;
// ENDING_PHRASES + the follow-up/refusal-limit rules live in endingPhrases.js (RQ-038).

// 2026-08-30 (real live incident): "Hello Lab" (a garbled, nonsense
// transcript) led the model to call toggle_tv(volume=11) - the 11 wasn't
// grounded in the saved transcript text at all, most likely the model
// reacting to raw audio it heard as "channel 11" and substituting the
// only numeric TV control it actually has. Unlike RESTING_PHRASES/
// ENDING_PHRASES this isn't a fixed vocabulary - a resident can request
// any volume level in many ways - so this only requires the word "volume"
// (or an unambiguous loud/quiet/mute cue) to appear, not a specific phrase.
// VOLUME_PHRASES lives in realtimeRoomControls.js (shared with adjust_tv_volume).

// TV power is grounded separately from TV volume. The model's `state`
// argument is not evidence that the resident asked for power: exact-volume
// calls also carry state="on", and live wake/goodbye turns have previously
// been mistranslated into toggle_tv calls. Require the resident's own words
// to name the TV, an explicit power verb and the same direction the model
// supplied. Negated requests fail closed.
const TV_POWER_NEGATION = /\b(?:don'?t|do\s+not|not|never|no)\b/i;
// Room 214, 2026-10-15: "Turn my TV on." / "Turn off my TV." were refused until repeated with "the".
const TV_NOUN = "(?:(?:the|my|our|this|that)\\s+)?(?:tv|television)";
const TV_POWER_PATTERNS = {
  on: [
    new RegExp(`\\b(?:turn|switch|power|put)\\s+${TV_NOUN}\\s+(?:back\\s+)?on\\b`, "i"),
    new RegExp(`\\b(?:turn|switch|power|put)\\s+(?:back\\s+)?on\\s+${TV_NOUN}\\b`, "i"),
  ],
  off: [
    new RegExp(`\\b(?:turn|switch|power|shut)\\s+${TV_NOUN}\\s+off\\b`, "i"),
    new RegExp(`\\b(?:turn|switch|power|shut)\\s+off\\s+${TV_NOUN}\\b`, "i"),
  ],
};

// A thermostat change needs a temperature word, a number, or a plain yes (to Aria's own "seventy-two?").
// No transcript at all is not blocked (no evidence either way), the same as the light guard.
const TEMPERATURE_WORDS = /\b(temp|temperature|thermostat|heat|heater|ac|a\/c|air|cool|cooler|cold|colder|warm|warmer|hot|hotter|degrees?|chilly|freezing|stuffy|up|down)\b|\d/i;
const PLAIN_YES = /\b(yes|yeah|yep|sure|okay|ok|please|do it|go ahead)\b/i;
export function temperatureRequestIsGrounded(text) {
  const heard = String(text || "").trim();
  return !heard || TEMPERATURE_WORDS.test(heard) || PLAIN_YES.test(heard);
}

export function tvPowerRequestIsGrounded(text, state) {
  const heard = String(text || "").trim();
  if (!TV_POWER_PATTERNS[state] || TV_POWER_NEGATION.test(heard)) return false;
  return TV_POWER_PATTERNS[state].some((pattern) => pattern.test(heard));
}

// 2026-08-23: "echo_like" (short, resembles Aria's own speech) genuinely
// suggests mishearing - ask to repeat. Other suspect reasons (a short but
// non-echoing fragment) are less about mishearing and more about wanting
// a quick check before acting - phrased as confirmation, not "did I mishear".
function suspectMessage(ctx) {
  return ctx?.turn_suspect_reason === "echo_like"
    ? "I want to make sure I heard that right — could you say that again?"
    : "Just to double-check — is that what you'd like me to do?";
}

export async function executeDeviceTool({ name, args, ctx }) {
  const room = ctx?.room;
  const residentId = ctx?.resident_id;
  const kioskId = ctx?.kiosk_id;

  if (CONSEQUENTIAL_DEVICE_TOOLS.has(name) && ctx?.turn_suspect) {
    return { ok: false, message: suspectMessage(ctx) };
  }

  if (name === "get_room_status") {
    if (!room) return { ok: false, message: "no room context — I can't check the room devices here." };
    const r = await fetch(`${API}/devices/public/room/${encodeURIComponent(room)}/inventory`);
    if (!r.ok) return { ok: false, message: `couldn't reach the room devices (${r.status}).` };
    return roomStatusResult(await r.json());
  }
  if (name === "configure_tv_remote") {
    return executeTvRemoteSetup(args, ctx);
  }
  if (name === "adjust_room_temperature") {
    if (!room) return { ok: false, message: "no room context — I can't reach the climate control here." };
    // Room 214 rt_r7yjpasv 06:14 CDT 2026-10-21: "Give me a turn." became "set 72" - nothing in the sentence
    // asked for a temperature, and a missing target used to default to 72. Both now ask instead.
    if (!Number.isFinite(Number(args.target_f)) || !temperatureRequestIsGrounded(ctx?.last_user_text)) {
      return { ok: false, message: "I didn't hear a clear temperature request, so I didn't change the thermostat. Ask what temperature they want." };
    }
    const targetF = Math.max(60, Math.min(85, Number(args.target_f)));
    const found = await findDeviceGuarded({ room, kind: "thermostat", noun: "thermostat", tool: name, args, ctx });
    if (found.fail) return found.fail;
    const r = await postRoomCommand(room, "temperature", targetF, "thermostat", ctx?.session_id, found.device?.device_id);
    if (!r.ok) return { ok: false, message: `couldn't reach the AC (${r.status}). I'll let the nurse know.` };
    return { ok: true, message: `set the room to ${targetF} degrees.` };
  }
  if (name === "toggle_light") {
    if (!room) return { ok: false, message: "no room context — I can't reach the lights here." };
    return handleToggleLight(room, args, ctx, postRoomCommand);
  }
  if (name === "toggle_tv") {
    if (!room) return { ok: false, message: "no room context — I can't reach the TV here." };
    const heard = (ctx?.last_user_text || "").trim();
    const powerGrounded = tvPowerRequestIsGrounded(heard, args.state);
    const volumeGrounded = typeof args.volume === "number" && VOLUME_PHRASES.test(heard);
    if (!powerGrounded && !volumeGrounded) {
      return { ok: false, message: "I didn't hear a clear TV power or volume request, so I didn't change the TV." };
    }
    // Same lookup-before-command shape as toggle_light (2026-09-06 kiosk
    // multi-light bug) - a room with more than one TV must fail closed on
    // an honest ambiguity message, not guess by posting `kind` alone.
    const tv = await findDeviceGuarded({ room, kind: "tv", noun: "TV", tool: name, args, ctx });
    if (tv.fail) return tv.fail;
    if (!tv.device) return { ok: false, message: "there's no TV set up in this room yet." };
    let powerResult = null;
    if (powerGrounded) {
      const r = await postRoomCommand(room, "power", args.state, "tv", ctx?.session_id, tv.device.device_id);
      if (!r.ok) return { ok: false, message: `couldn't reach the TV (${r.status}).` };
      powerResult = await r.json().catch(() => null);
    }
    let volumeResult = null;
    if (volumeGrounded) {
      const r = await postRoomCommand(room, "volume", Math.max(0, Math.min(100, args.volume)), "tv", ctx?.session_id, tv.device.device_id);
      if (!r.ok) return { ok: false, message: `couldn't reach the TV volume (${r.status}).` };
      volumeResult = await r.json().catch(() => null);
    }
    const messages = [];
    if (powerGrounded) {
      messages.push(powerResult?.verified === true
        ? `turned the TV ${args.state}`
        : `sent the TV ${args.state} command; this TV did not verify the change`);
    }
    if (volumeGrounded) {
      messages.push(volumeResult?.verified === true && typeof volumeResult?.state?.volume === "number"
        ? `verified the volume at ${volumeResult.state.volume}`
        : `sent the volume ${Math.max(0, Math.min(100, args.volume))} command; this TV did not report its resulting level`);
    }
    return { ok: true, message: `${messages.join(" and ")}.` };
  }
  if (ROOM_CONTROL_TOOLS.has(name)) {
    return executeRoomControlTool(name, args, ctx, { postRoomCommand });
  }
  if (name === "set_tv_input") {
    if (!room) return { ok: false, message: "no room context — I can't reach the TV here." };
    const tvLookup = await findDeviceGuarded({ room, kind: "tv", noun: "TV", tool: name, args, ctx });
    if (tvLookup.fail) return tvLookup.fail;
    const tv = tvLookup.device;
    // Verify the device actually declares this input before calling -
    // per the tool's own instruction, a device that doesn't support it
    // should get an honest "not available", not a failed/ignored command.
    if (!tv || !(tv.capabilities || []).includes("input")) {
      return { ok: false, message: "this TV doesn't support switching inputs." };
    }
    if (!(tv.inputs || []).some((i) => i.toLowerCase() === String(args.input).toLowerCase())) {
      return { ok: false, message: `this TV doesn't have a "${args.input}" input — it has: ${(tv.inputs || []).join(", ") || "none listed"}.` };
    }
    const r = await postRoomCommand(room, "input", args.input, "tv", ctx?.session_id, tv.device_id);
    if (!r.ok) return { ok: false, message: `couldn't switch the input (${r.status}).` };
    return { ok: true, message: `switched the TV to ${args.input}.` };
  }
  if (name === "call_for_help") {
    // Enriches the resident's OPEN help event (never mints a duplicate,
    // never counts as a human press) and triggers a real, durable nursing
    // dispatch. Aria's wording below is derived from the dispatch's actual
    // delivery state - not from having "tried". (Level 1 directive 2026-09-07.)
    const r = await fetch(`${API}/alerts/ai-escalate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        reason: args.reason || "AI-initiated call for help during conversation",
        severity: args.severity === "emergency" ? "emergency" : "assist",
        department: "Care/Nursing",
        resident_id: residentId || null,
        kiosk_id: kioskId || null,
        room: room || null,
        alert_id: ctx?.alert_id || null,
        activation_id: ctx?.activation_id || null,
        session_id: ctx?.session_id || null,
      }),
    });
    if (!r.ok) {
      return { ok: false, message: `I wasn't able to reach the care team just now — please press the red button. I'm staying right here with you.` };
    }
    const d = await r.json().catch(() => ({}));
    if (d.wording_state === "paged") {
      return { ok: true, message: "A nurse has been paged. I'm right here with you." };
    }
    if (d.wording_state === "sent") {
      return { ok: true, message: "I've sent your request to the care team, and I'm staying right here with you." };
    }
    // failed (or unknown) - do NOT claim anyone was paged
    return { ok: false, message: "I wasn't able to reach the care team just now — please press the red button. I'm staying right here with you." };
  }
  if (name === "mark_resting") {
    // Structural grounding (2026-08-30) - see RESTING_PHRASES above. No
    // backend side-effect otherwise; the function existing (once granted)
    // tells the model to fall silent, and useRealtimeVoice.js disables
    // server auto-response for real.
    const heard = (ctx?.last_user_text || "").trim();
    if (!heard || !RESTING_PHRASES.test(heard)) {
      return { ok: false, message: "Just to make sure — would you like me to go quiet for a bit?" };
    }
    return { ok: true, message: "going quiet now. I'll be right here when you need me." };
  }
  if (name === "get_current_time") {
    const tz = ctx?.facility_tz || "America/New_York";
    const label = ctx?.facility_label || "the facility";
    try {
      const d = new Date();
      const fmt = new Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        weekday: "long", month: "long", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit",
      });
      return { ok: true, message: `it's ${fmt.format(d)} at ${label}.` };
    } catch {
      return { ok: true, message: `it's ${new Date().toLocaleString()}.` };
    }
  }
  if (name === "get_weather") {
    const q = new URLSearchParams();
    if (args.location) q.set("label", args.location);
    if (Number(args.days) > 1) q.set("days", String(Math.min(7, Math.round(Number(args.days)))));
    const qs = q.toString() ? `?${q}` : "";
    const r = await fetch(`${API}/weather/current${qs}`);
    if (!r.ok) return { ok: false, message: `couldn't reach the weather service (${r.status}).` };
    const w = await r.json();
    return { ok: true, message: w.narrative || `${w.temperature_f}° and ${w.condition}.` };
  }
  if (name === "research_topic") {
    let token = null;
    try { token = localStorage.getItem("caos_token"); } catch { /* no storage */ }
    const r = await fetch(`${API}/research`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ question: args.question || "", resident_id: residentId || null, session_id: ctx?.session_id || null }),
    });
    if (r.status === 403 || r.status === 429) {
      return { ok: false, message: "I can't look that up right now." };
    }
    if (!r.ok) return { ok: false, message: `couldn't reach the research service (${r.status}).` };
    const j = await r.json();
    return shapeResearchResult(j);
  }
  if (name === "set_timer") {
    const minutes = Math.max(0.1, Math.min(720, Number(args.minutes) || 5));
    const label = (args.label || "your reminder").toString().slice(0, 200);
    const r = await fetch(`${API}/timers/public`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ minutes, label, resident_id: residentId || null, room: room || null, kiosk_id: kioskId || null }),
    });
    if (!r.ok) return { ok: false, message: `couldn't set the timer (${r.status}).` };
    return { ok: true, message: `okay, I'll remind you in ${minutes < 1 ? `${Math.round(minutes * 60)} seconds` : `${minutes} minutes`}.` };
  }
  if (name === "update_preferred_name") {
    if (ctx?.turn_suspect) {
      // Echo/VAD-overlap risk on the turn that triggered this - do not save.
      return { ok: false, message: ctx.turn_suspect_reason === "echo_like" ? "I want to make sure I heard that right over the background noise — could you say your name again for me?" : "Just to double-check — is that the name you'd like me to use?" };
    }
    const newName = (args.preferred_name || "").toString().trim().slice(0, 60);
    if (!newName) return { ok: false, message: "I didn't catch the name." };
    // TSB-001: ground the claimed correction in what the resident actually
    // said, not just the model's self-reported args - a challenge question
    // ("Why do you call me Ellie?", "Who told you to call me Ellie?") can
    // mention the name too, so also reject turns that read as a question
    // rather than a stated correction. Both real TSB-001 failures were
    // interrogative turns; this rejects both without touching a genuine
    // "my name is X, not Y" / "call me X" correction.
    const heard = (ctx?.last_user_text || "").trim();
    const looksLikeQuestion = /^\s*(why|who|what|when|where|how)\b/i.test(heard);
    if (!heard || !heard.toLowerCase().includes(newName.toLowerCase()) || looksLikeQuestion) {
      return { ok: false, message: "Sorry, I want to make sure I have that right — could you tell me again what you'd like me to call you?" };
    }
    if (!residentId) return { ok: true, message: `okay, I'll call you ${newName} from now on.` };
    const r = await fetch(`${API}/residents/${residentId}/preferred-name`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ preferred_name: newName, room: ctx?.room || null, session_id: ctx?.session_id || null }),
    });
    if (!r.ok) return { ok: false, message: `I'll call you ${newName} from now on (didn't quite save it though).` };
    return { ok: true, message: `okay, ${newName} it is. Saved.` };
  }
  if (name === "end_call" || name === "end_conversation") {
    // 2026-08-22 (real bug, confirmed live): a phantom echo turn ("and")
    // reached this tool and hung up on the resident mid-session. Ending
    // the call is NOT an emergency action, so a suspect turn gets a
    // natural confirmation instead of silent compliance -
    // handleFunctionCall (realtimeMessageHandler.js) only tears down the
    // connection when ok:true comes back from here.
    // RQ-041: a turn flagged only "uncertain_fragment" (short speech that
    // overlapped Aria and does NOT resemble her own words) is accepted when
    // its words are an explicit ending ("Goodnight, goodbye."). echo_like /
    // repeated_tiny_fragments stay refused - the 2026-08-22 phantom "and".
    // Not for a soft ending ("That's all") - Room 214 rt_qppzkpzp: an uncertain overlap heard as "That's all" is not a goodbye.
    const heardEnding = ctx?.turn_suspect_reason === "uncertain_fragment" && ENDING_PHRASES.test((ctx?.last_user_text || "").trim())
      && !isSoftEnding(ctx?.last_user_text);
    if (ctx?.turn_suspect && !heardEnding) {
      return { ok: false, message: "Just to double-check — did you want me to end our conversation?" };
    }
    // Structural grounding (2026-08-30) - see ENDING_PHRASES above. A real
    // live session ended on the transcript "It's gonna find me." - nothing
    // about that utterance supports ending the call.
    const grounded = checkEnding(ctx?.session_id, (ctx?.last_user_text || "").trim());
    if (!grounded.ok) return grounded;
    // The actual hang-up happens in the calling layer (handleFunctionCall)
    // because it needs access to the peer connection. Returning here just
    // gives the model its short verbal goodbye to speak.
    return { ok: true, message: ctx?.goodbye_line || "Okay, bye for now." };
  }
  return undefined;
}
